import React, { useEffect, useState, useMemo } from "react";
import ReactDOMServer from "react-dom/server";
import LandingPageBody from "./landing-page-body";
import { BreadcrumbComponent } from "../../../common/components";
import { routes } from "../../../constants/routes";
import WhatWeOffer from "./sections/whatWeOffer";
import WhoWeAre from "./sections/whoWeAre";
import CoreValuesAndPrinciples from "./sections/ValuesAndPrinciples";
import "./landing-page.scss";
import { useLogin } from "../../../common/hooks/useUser";
import Contact from "./sections/contact";
import PartnerSuccessMetrics from "./sections/partnerSuccessMetrics";
import OurServices from "./sections/ourServices";
import MainSection from "./sections/main-section";
import FooterSection from "./sections/footer-section";
import TestimonialSection from "./sections/testimonials-section";
import SaveAndSharePopup from "./save-and-share-popup";
import store from "../../../store";
import { AlertComponent } from "../../../common/components";
import { useSetLoading } from "../../../common/hooks/appearance";
import {
  fetchLandingPageDetails,
  saveLandingPageDetails,
  uploadImageToS3,
} from "../../../api/campaign";
import { useResourcePermissions } from "../../../common/hooks/resource-permissions";
import MODULES from "../../../constants/modules";
import { adjustHexBrightnessByPercent } from "../../../utils/themeUtils";
import EmailSetUpBanner from "../../../common/components/email-setup-required-banner/email-setup-banner";

interface Section {
  id: string;
  title: string;
}

const CampaignLandingPage = () => {
  const breadcrumbLink = [
    { label: "Campaigns", link: routes.CAMPAIGNLIST },
    { label: "Landing Page", link: "#" },
  ];

  const [downloadFile, setDownloadFile] = useState<File | null>(null);
  const [primaryColor, setPrimaryColor] = useState("#726545");
  const [secondaryColor, setSecondaryColor] = useState("#887952");
  const [accentBorderColor, setAccentBorderColor] = useState("#9a8348");
  const [logoUrl, setLogoUrl] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [showSaveAndSharePopup, setShowSaveAndSharePopup] = useState(false);
  const [tenantId, setTenantId] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [imageFiles, setImageFiles] = useState({});
  const { canRead, canWrite, canDelete } = useResourcePermissions(
    MODULES.LANDINGPAGE
  );

  const [mainSectionData, setMainSectionData] = useState({
    id: "main-section",
    title: "Your Heading",
    subTitle: "Sub Heading",
    btnUrl: "",
    imageUrl: "",
  });

  const [whoWeAreData, setWhoWeAreData] = useState({
    id: "who-we-are",
    title: "Who We Are?",
    description: "Body Text...",
    btnUrl: "",
  });

  const [whatWeOfferData, setWhatWeOfferData] = useState({
    id: "what-we-offer",
    title: "What We Offer?",
    bodyText: "Body Text",
    btnUrl: "",
    imageUrl: "",
  });

  const [valuesPrinciplesData, setValuesPrinciplesData] = useState({
    id: "values-principles",
    title: "Core Values & Principles",
    principles: "Body Text",
    imageUrl: `https://afp-crm-qa.s3.us-east-1.amazonaws.com/Camapaign-Landing-Pages-Assets/CoreValuesPrincplesBackgroundImage.jpg`,
    btnUrl: "",
  });

  const [previousValuesPrinciplesData, setPreviousValuesPrinciplesData] =
    useState({ principles: "Body Text" });

  const [testimonialsData, setTestimonialsData] = useState({
    id: "testimonials",
    title: "Testimonials",
    testimonials: [
      {
        author: "Enter Name",
        designation: "Enter Designation",
        feedback: "Write review",
      },
      {
        author: "Oberon Shaw, MCH",
        designation: "Head of Talent Acquisition, North America",
        feedback: "This platform changed the way we do business.",
      },
      {
        author: "Oberon Shaw, MCH",
        designation: "Head of Talent Acquisition, North America",
        feedback: "This platform changed the way we do business.",
      },
    ],
  });

  const [previousTestMenialData, setPreviousTestMenialData] =
    useState(testimonialsData);

  const [ourServiceData, setOurServiceData] = useState({
    id: "our-service",
    title: "Our Services",
    isTitleEditing: false,
    subHeading: "SubHeading",
    isSubHeadingEditing: false,
    services: [
      {
        id: 1,
        title: "Consulting",
        isTitleEditing: false,
        description: "Expert advice for your business",
        isDescriptionEditing: false,
        isRemoveService: false,
      },
      {
        id: 2,
        title: "24/7 Support",
        isTitleEditing: false,
        description: "We’re always here to help",
        isDescriptionEditing: false,
        isRemoveService: false,
      },
    ],
    leadCaptureFormData: {
      id: 0,
      formName: "",
      formLink: "",
      qrCodeUrl: "",
    },
  });

  const [partnerSuccessMetricsData, setPartnerSuccessMetricsData] = useState({
    id: "partner-success-metrics",
    title: "Partner Success Metrics",
    isTitleEditing: false,
    metrics: [
      {
        id: 1,
        value: "25+",
        isValueEditing: false,
        label: "Years of Trusted Service",
        isLabelEditing: false,
      },
      {
        id: 2,
        value: "$2B",
        isValueEditing: false,
        label: "In Insurance Secured",
        isLabelEditing: false,
      },
      {
        id: 3,
        value: "95%",
        isValueEditing: false,
        label: "Client Retention",
        isLabelEditing: false,
      },
      {
        id: 4,
        value: "100,000+",
        isValueEditing: false,
        label: "Funeral Home, Agency And Agent Affiliates NationWide",
        isLabelEditing: false,
      },
    ],
  });

  const [contactData, setContactData] = useState({
    title: "Contact Us",
    email: "support@example.com",
    phone: "+1-234-567-8900",
    address: "123 Business Rd, Tech City",
  });

  const [sectionVisibility, setSectionVisibility] = useState<
    Record<string, boolean>
  >({
    "main-section": false,
    "who-we-are": false,
    "what-we-offer": false,
    "values-principles": false,
    testimonials: false,
    "our-service": false,
    "partner-success-metrics": false,
  });

  const loginDetails = useLogin();
  const isEmailConfigured = loginDetails?.isEmailConfigured;

  const saveLandingPageDetailsApi = async (data: any) => {
    const uploadedFile = data.get("file");
    if (!uploadedFile || !(uploadedFile instanceof File)) {
      setAlertMessage("HTML file is required to save landing page details.");
      setAlertType("error");
      setAlertOpen(true);
      return;
    }
    setLoading(true);
    try {
      const response = await saveLandingPageDetails(data);
      if (response.status === 200 || response.status === 201) {
        setResourceId(response?.data?.data?.id);
        setLandingPageHtmlLink(response?.data?.data?.landing_page_url || "");
        setQRCodeURL(response?.data?.data?.qr_code_url || "");
        setAlertMessage(
          `Landing page ${response.status === 201 ? "created" : "updated"} successfully.`
        );
        setAlertType("success");
        setAlertOpen(true);
        if (response?.data?.data?.landing_page_url)
          return response?.data?.data?.landing_page_url;
      } else {
        setAlertMessage("Failed to save landing page details.");
        setAlertType("error");
        setAlertOpen(true);
      }
      return "";
    } catch (error: any) {
      console.error("Error saving landing page details:", error);
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving landing page details."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
    return "";
  };

  // const getTotalSectionsData = () => {

  //   return {
  //     sections: [
  //       {
  //         ...mainSectionData,
  //         isHided: sectionVisibility["main-section"] ?? false,
  //       },
  //       {
  //         ...whoWeAreData,
  //         isHided: sectionVisibility["who-we-are"] ?? false,
  //       },
  //       {
  //         ...whatWeOfferData,
  //         isHided: sectionVisibility["what-we-offer"] ?? false,
  //       },
  //       {
  //         ...valuesPrinciplesData,
  //         isHided: sectionVisibility["values-principles"] ?? false,
  //       },
  //       {
  //         ...testimonialsData,
  //         isHided: sectionVisibility["testimonials"] ?? false,
  //       },
  //       {
  //         ...ourServiceData,
  //         isHided: sectionVisibility["our-service"] ?? false,
  //       },
  //       {
  //         ...partnerSuccessMetricsData,
  //         isHided: sectionVisibility["partner-success-metrics"] ?? false,
  //       },
  //     ],
  //   }
  // }

  const uploadImageToS3Api = async (imageFiles: any) => {
    setLoading(true);
    const sectionOrder = ["main-section", "what-we-offer", "values-principles"];
    const formData = new FormData();
    const files: File[] = [];
    sectionOrder.forEach((section) => {
      const file = imageFiles?.[section];
      if (file) {
        formData.append("files", file); // appends under 'files[]'
        files.push(file);
        formData.append("sections[]", section);
      }
    });
    try {
      const response = await uploadImageToS3(formData);
      if (response.status === 200) {
        const uploadedFilesMap = response?.data?.data;
        if (uploadedFilesMap) {
          if (uploadedFilesMap["main-section"]) {
            setMainSectionData((prev) => ({
              ...prev,
              imageUrl: uploadedFilesMap["main-section"],
            }));
          }

          if (uploadedFilesMap["what-we-offer"]) {
            setWhatWeOfferData((prev) => ({
              ...prev,
              imageUrl: uploadedFilesMap["what-we-offer"],
            }));
          }

          if (uploadedFilesMap["values-principles"]) {
            setValuesPrinciplesData((prev) => ({
              ...prev,
              imageUrl: uploadedFilesMap["values-principles"],
            }));
          }
        }
        return uploadedFilesMap;
      }
    } catch (err: any) {
      setAlertMessage(
        err?.response?.data?.message || "Failed to upload images"
      );
      setAlertType("error");
      setAlertOpen(true);
      console.error("Upload failed", err);
      return {};
    } finally {
      setLoading(false);
    }
    return {};
  };

  const [landingPageData, setLandingPageData] = useState({
    sections: [
      {
        ...mainSectionData,
        isHided: sectionVisibility["main-section"] ?? false,
      },
      {
        ...whoWeAreData,
        isHided: sectionVisibility["who-we-are"] ?? false,
      },
      {
        ...whatWeOfferData,
        isHided: sectionVisibility["what-we-offer"] ?? false,
      },
      {
        ...valuesPrinciplesData,
        isHided: sectionVisibility["values-principles"] ?? false,
      },
      {
        ...testimonialsData,
        isHided: sectionVisibility["testimonials"] ?? false,
      },
      {
        ...ourServiceData,
        isHided: sectionVisibility["our-service"] ?? false,
      },
      {
        ...partnerSuccessMetricsData,
        isHided: sectionVisibility["partner-success-metrics"] ?? false,
      },
    ],
  });

  useEffect(() => {
    setLandingPageData({
      sections: [
        {
          ...mainSectionData,
          isHided: sectionVisibility["main-section"] ?? false,
        },
        {
          ...whoWeAreData,
          isHided: sectionVisibility["who-we-are"] ?? false,
        },
        {
          ...whatWeOfferData,
          isHided: sectionVisibility["what-we-offer"] ?? false,
        },
        {
          ...valuesPrinciplesData,
          isHided: sectionVisibility["values-principles"] ?? false,
        },
        {
          ...testimonialsData,
          isHided: sectionVisibility["testimonials"] ?? false,
        },
        {
          ...ourServiceData,
          isHided: sectionVisibility["our-service"] ?? false,
        },
        {
          ...partnerSuccessMetricsData,
          isHided: sectionVisibility["partner-success-metrics"] ?? false,
        },
      ],
    });
  }, [
    mainSectionData,
    whoWeAreData,
    whatWeOfferData,
    valuesPrinciplesData,
    testimonialsData,
    ourServiceData,
    partnerSuccessMetricsData,
    sectionVisibility,
  ]);

  const handleSave = async () => {
    const appState = store.getState();
    const host = `${appState.server.serverProtocol}://${appState.server.serverUrl}:${appState.server.serverPort}`;
    let imageUrlsData;
    if (imageFiles && Object.keys(imageFiles)?.length > 0) {
      imageUrlsData = await uploadImageToS3Api(imageFiles);
      if (!imageUrlsData || Object.keys(imageUrlsData).length === 0) {
        setAlertMessage("Failed to upload images. Please try again.");
        setAlertType("error");
        setAlertOpen(true);
        return;
      }
    }
    let updatedWhatWeOfferData = whatWeOfferData;
    let updatedValuesPrinciplesData = valuesPrinciplesData;
    let updatedMainSectionData = mainSectionData;
    if (imageUrlsData && Object.keys(imageUrlsData).length > 0) {
      // ❗ If upload failed or returned nothing, exit early
      if (imageUrlsData["what-we-offer"]) {
        updatedWhatWeOfferData = {
          ...whatWeOfferData,
          imageUrl: imageUrlsData["what-we-offer"] || whatWeOfferData.imageUrl,
        };
      }
      if (imageUrlsData["values-principles"]) {
        updatedValuesPrinciplesData = {
          ...valuesPrinciplesData,
          imageUrl:
            imageUrlsData["values-principles"] || valuesPrinciplesData.imageUrl,
        };
      }
      if (imageUrlsData["main-section"]) {
        updatedMainSectionData = {
          ...mainSectionData,
          imageUrl: imageUrlsData["main-section"] || mainSectionData.imageUrl,
        };
      }
    }
    const htmlWithoutEdit = ReactDOMServer.renderToStaticMarkup(
      <div style={{ fontFamily: "Roboto, sans-serif", overflowX: "hidden" }}>
        <LandingPageBody
          isForSharing={true}
          primaryColor={primaryColor}
          accentColor={secondaryColor}
          logoUrl={logoUrl}
        />
        {!sectionVisibility?.["main-section"] && (
          <MainSection
            isForSharing={true}
            title={mainSectionData?.title}
            mainSectionData={updatedMainSectionData}
            setMainSectionData={setMainSectionData}
            sectionVisibility={sectionVisibility}
            setSectionVisibility={setSectionVisibility}
            primaryColor={primaryColor}
            accentColor={secondaryColor}
          />
        )}
        {!sectionVisibility?.["who-we-are"] && (
          <WhoWeAre
            title={whoWeAreData.title}
            isForSharing={true}
            whoWeAreData={whoWeAreData}
            setWhoWeAreData={setWhoWeAreData}
            sectionVisibility={sectionVisibility}
            setSectionVisibility={setSectionVisibility}
            primaryColor={primaryColor}
            accentColor={secondaryColor}
          />
        )}
        {!sectionVisibility?.["what-we-offer"] && (
          <WhatWeOffer
            title={whatWeOfferData.title}
            isForSharing={true}
            whatWeOfferData={updatedWhatWeOfferData}
            setWhatWeOfferData={setWhatWeOfferData}
            sectionVisibility={sectionVisibility}
            setSectionVisibility={setSectionVisibility}
            primaryColor={primaryColor}
            accentColor={secondaryColor}
          />
        )}
        {!sectionVisibility?.["values-principles"] && (
          <CoreValuesAndPrinciples
            title={valuesPrinciplesData.title}
            isForSharing={true}
            valuesPrinciplesData={updatedValuesPrinciplesData}
            setValuesPrinciplesData={setValuesPrinciplesData}
            sectionVisibility={sectionVisibility}
            setSectionVisibility={setSectionVisibility}
            primaryColor={primaryColor}
            accentColor={secondaryColor}
          />
        )}
        {!sectionVisibility["testimonials"] && (
          <TestimonialSection
            title={testimonialsData.title}
            isForSharing={true}
            testimonialData={testimonialsData}
            setTestimonialData={setTestimonialsData}
            sectionVisibility={sectionVisibility}
            setSectionVisibility={setSectionVisibility}
            primaryColor={primaryColor}
            accentColor={secondaryColor}
          />
        )}
        {!sectionVisibility["our-service"] && (
          <OurServices
            isForSharing={true}
            ourServiceData={ourServiceData}
            setOurServiceData={setOurServiceData}
            sectionVisibility={sectionVisibility}
            setSectionVisibility={setSectionVisibility}
            primaryColor={primaryColor}
            accentColor={secondaryColor}
          />
        )}
        {!sectionVisibility["partner-success-metrics"] && (
          <PartnerSuccessMetrics
            isForSharing={true}
            partnerSuccessMetricsData={partnerSuccessMetricsData}
            setPartnerSuccessMetricsData={setPartnerSuccessMetricsData}
            sectionVisibility={sectionVisibility}
            setSectionVisibility={setSectionVisibility}
            primaryColor={primaryColor}
            accentColor={secondaryColor}
          />
        )}
        <Contact
          isForSharing={true}
          contactData={contactData}
          setContactData={setContactData}
          primaryColor={primaryColor}
          accentColor={secondaryColor}
        />
        <FooterSection
          primaryColor={primaryColor}
          accentColor={secondaryColor}
          logoUrl={logoUrl}
          companyName={companyName}
        />
      </div>
    );

    const fullHtml = `<!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Landing Page</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap" rel="stylesheet">

      <style>
        body {
          margin: 0;
          font-family: Roboto, sans-serif;
           -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
        }
          p {
      font-weight: 400; /* Regular weight */
    }
          .error-message{
        color: red;
        font-size: 12px;
        margin-top: 5px;
      }
           input,
    textarea {
      caret-color: #000 !important;
    }
       @media only screen and (max-width: 1023px) {
          .landing-page-main-section{
              display: block !important;
              height: 100% !important;
          }
        #your-section {
          padding: 20px !important;
        }
          .landing-page-main-section-one{
            width: 100% !important;
            max-width: 100% !important;
            margin-top: 52px !important;
          }
          .landing-page-main-section-one-main-subtitle{
              width: 100% !important;
              justify-content: flex-start !important;
              align-items: end !important;
          }
          .landing-page-main-section-two{
            width: 100% !important;
            margin-top: 20px !important;
          }
            #who-we-are{
              padding: 20px !important;
            }
          #who-we-are   textarea{
            width: 100% !important;
            height: 200px !important;
          }
          #what-we-offer-wrapper{
            padding: 20px !important;
          }
         #what-we-offer-body-wrapper{
            display: block !important;
            width: 100% !important;
            height: 100% !important;
          }
          #what-we-offer-body-wrapper h3{
            font-size: 22px !important;
          }
          .what-we-offer-body-wrapper-image-upload{
            width: 100% !important;
            margin-top: 72px !important;
          }
          .what-we-offer-body-wrapper-text-upload{
            width: 100% !important;
            margin-top: 22px !important;
          }
          #values-and-principles-body-wrapper{
            width: 100% !important;
            height: 100% !important;
            margin-top: 82px !important;
            display: block !important;
          }
          .values-and-principles-body-wrapper-section{
            width: 100% !important;
            height: 100% !important;
            margin-top: 22px !important;
          }
          .values-and-principles-body-wrapper-section  h3{
            font-size: 22px !important;
          }
          .image-upload-container-values-principles{
            width: 100% !important;
            margin-top: 72px !important;
          }
          .who-we-are-section{
            height: 100% !important;
            padding: 0px 30px !important;
          }
          .who-we-are-section-heading{
            margin-top: 32px !important;
          }
          .who-we-are-section-cards{
            width: 100% !important;
            height: 100% !important;
            padding: 20px !important;
                  justify-content: flex-start !important;
          }
          .who-we-are-section-card{
              min-width: calc(100% - 40px) !important;
          }
          .who-we-are-section-card p{
                width: 100% !important;
          }
          .who-we-are-section-card textarea{
                width:calc(100% - 20px) !important;
          }
          #our-service{
          padding: 20px !important;
          }
          .service-card-section{
            width: 100% !important;
            height: 100% !important;
            margin: 0px !important;
            align-items: center !important;
          }
          .service-card{
            width: calc(100% - 40px) !important;
            height: 100% !important;
          }
          .service-card  p{
              width: 100% !important;
            }
          .service-card  textarea{
              width:calc(100% - 20px) !important;
            }
          .service-card-button{
            width: calc(100% - 100px) !important;
          }
          .partner-success-metrics-title{
            justify-content: center !important;
            margin-top: 20px !important;
          }
           .partner-success-metrics-title h3{
            font-size: 22px !important;
          }
        .partner-success-metrics-cards{
          display: block !important;
        }
          .partner-success-metrics-card{
            border: none !important;
            width: calc(100% - 40px) !important;
          }
            .partner-success-metrics-card p{
            text-align: center !important;
            }
          #contact{
          padding: 20px 20px 0px 20px !important;
          }
         .contact-form-container{
            display: block !important;
            height: 100% !important;
            width: 100% !important;
          }
          .contact-form-container-section{
            width: calc(100% - 40px) !important;
            height: 100% !important;
            padding: 20px !important;
            margin: 0px !important;
          }
          
      }

      @media (min-width: 760px) and (max-width: 1023px) {
    .who-we-are-section {
          padding: 0px 30px !important;
      
        }
         .who-we-are-section-card {
              min-width: calc(100% - 60%) !important;
            }
        .service-card-section {
          align-items: center !important;
        }
        .service-card {
            width: calc(100% - 60%) !important;
        }
}

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      </style>
    </head>
    <body>
    <div id="form-loader" style="display:none; position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background-color: rgba(255, 255, 255, 0.8); z-index: 9999; align-items: center; justify-content: center;">
    <div class="spinner" style="border: 4px solid #f3f3f3; border-top: 4px solid rgb(33, 39, 42); border-radius: 50%;
        width: 40px; height: 40px; animation: spin 1s linear infinite;"></div>
    </div>
      ${htmlWithoutEdit}
    <script>
  document.addEventListener('DOMContentLoaded', function () {
    const submitBtn = document.getElementById("contact-submit-button");

    const firstNameEl = document.getElementById("firstName");
    const lastNameEl = document.getElementById("lastName");
    const contactNumberEl = document.getElementById("contactNumber");
    const emailEl = document.getElementById("email");
    const messageEl = document.getElementById("message");
    
    [firstNameEl, lastNameEl, contactNumberEl, emailEl, messageEl].forEach((el) => {
      el?.addEventListener("input", function () {
        const errorId = el.id + "-error-message";
        const errorEl = document.getElementById(errorId);
        if (errorEl) {
          errorEl.textContent = "";
        }
      });
    });
    
    submitBtn?.addEventListener("click", function () {
      const loader = document.getElementById("form-loader");

      const firstName = firstNameEl?.value.trim() || "";
      const lastName = lastNameEl?.value.trim() || "";
      const contactNumber = contactNumberEl?.value.trim() || "";
      const email = emailEl?.value.trim() || "";
      const message = messageEl?.value.trim() || "-";

      const emailValidationRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const nameRegex = /^[A-Za-z ]+$/;

      // Clear all previous error messages
      const fields = [firstNameEl, lastNameEl, contactNumberEl, emailEl, messageEl];
        fields.forEach(el => {
          const errorId = el.id + "-error-message";
          const errorEl = document.getElementById(errorId);
          if (errorEl) {
            errorEl.textContent = "";
          }
        });

      let hasError = false;

      // First Name
      if (!firstName) {
        showError(firstNameEl, "First name is required");
        hasError = true;
      } else if (!nameRegex.test(firstName)) {
        showError(firstNameEl, "Only letters are allowed");
        hasError = true;
      } else {
        showError(firstNameEl, "");
      }

      // Last Name
      if (!lastName) {
        showError(lastNameEl, "Last name is required");
        hasError = true;
      } else if (!nameRegex.test(lastName)) {
        showError(lastNameEl, "Only letters are allowed");
        hasError = true;
      } else {
        showError(lastNameEl, "");
      }

      // Contact Number
      if (!contactNumber) {
        showError(contactNumberEl, "Contact number is required!");
        hasError = true;
      } else if (!/^[0-9]{10}$/.test(contactNumber)) {
        showError(contactNumberEl, "Please enter a valid 10-digit number.");
        hasError = true;
      } else {
        showError(contactNumberEl, "");
      }

      // Email
      if (!email) {
        showError(emailEl, "Email is required");
        hasError = true;
      } else if (!emailValidationRegex.test(email)) {
        showError(emailEl, "Please enter a valid email address.");
        hasError = true;
      } else {
        showError(emailEl, "");
      }

      if (hasError) return;
      loader.style.display = "flex";

      // Prepare form data
      const formData = new FormData();
      formData.append("firstName", firstName);
      formData.append("lastName", lastName);
      formData.append("phoneNumber", contactNumber);
      formData.append("email", email);
      formData.append("companyName", "${companyName}");
      formData.append("partnerId", "${tenantId}");
      formData.append("message", message);

      let request = new XMLHttpRequest();
      request.open('POST', '${host}/api/v1/contact-us');
      request.onloadend = (event) => {
      loader.style.display = "none";
      const response=JSON.parse(request?.response)
      if(response?.success){
        document.getElementById("contact-submit-button").disabled = false;
        alert("Thank you for contacting us!");
        location.reload();
      }else{
        document.getElementById("contact-submit-button").disabled = false;
        alert(response?.data?.data?.message || "There was an error making the request.");
       }
      }   
      request.onerror = function(event) {
        loader.style.display = "none";
         document.getElementById("contact-submit-button").disabled = false;
         alert('There was an error making the request.');
       };
      request.send(formData);

      function showError(element, message) {
        const errorMessageElementName = element.id + '-error-message';
        const errorMessageElement = document.getElementById(errorMessageElementName);
        if (errorMessageElement) {
          errorMessageElement.textContent = message;
        }
      }
    });
  });
</script>


    </body>
    </html>`;

    const file = new File([fullHtml], `${companyName}-landing-page.html`, {
      type: "text/html",
    });
    setDownloadFile(file);
    const formData = new FormData();
    formData.append("file", file);
    const updatedLandingPageData = {
      ...landingPageData,
      sections: landingPageData.sections.map((section: Section) => {
        if (section.id === "main-section") {
          return {
            ...section,
            ...updatedMainSectionData,
            isHided: sectionVisibility["main-section"] ?? false,
          };
        } else if (section.id === "what-we-offer") {
          return {
            ...section,
            ...updatedWhatWeOfferData,
            isHided: sectionVisibility["what-we-offer"] ?? false,
          };
        } else if (section.id === "values-principles") {
          return {
            ...section,
            ...updatedValuesPrinciplesData,
            isHided: sectionVisibility["values-principles"] ?? false,
          };
        }
        return section;
      }),
    };
    formData.append("jsonData", JSON.stringify(updatedLandingPageData));
    if (resourceId) {
      formData.append("resourceId", JSON.stringify(resourceId));
    }
    const htmlFileUrl = await saveLandingPageDetailsApi(formData);
    setPreviousValuesPrinciplesData({
      principles: updatedValuesPrinciplesData?.principles,
    });
    setPreviousTestMenialData(testimonialsData);
    setImageFiles({}); // Reset image files after saving
    return htmlFileUrl;
  };

  const handleSaveAndShare = async () => {
    const htmlFileUrl = await handleSave();
    if (htmlFileUrl) {
      setShowSaveAndSharePopup(true);
    }
  };

  const handleDownload = () => {
    if (downloadFile) {
      const url = URL.createObjectURL(downloadFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadFile.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url); // Clean up
    } else {
      alert("Please click 'Save And Share' first to generate the file.");
    }
  };

  const { tenantDetails, userDetails } = useLogin();
  useEffect(() => {
    if (tenantDetails) {
      setPrimaryColor(tenantDetails.primaryColor || "#726545");
      setSecondaryColor(tenantDetails.accentColor || "#887952");
      const accentBColor = adjustHexBrightnessByPercent(
        tenantDetails.accentColor || "#887952",
        35
      );
      setAccentBorderColor(accentBColor || "#9a8348");
      setLogoUrl(tenantDetails?.thumbnailUrlTwo);
      setCompanyName(tenantDetails?.companyName);
      setTenantId(tenantDetails?.id || 0);
      setContactData((prev) => ({
        ...prev,
        email: tenantDetails?.email || "",
        phone: tenantDetails?.phoneNumber || "",
        address: `${tenantDetails?.address?.street_line_1 || "-"}, ${tenantDetails?.address?.street_line_2 || "-"}, ${tenantDetails?.address?.city || ""}, ${tenantDetails?.address?.state || ""}, ${tenantDetails?.address?.country || ""}, ${tenantDetails?.address?.postcode || ""}`,
      }));
    }
  }, [tenantDetails, userDetails]);

  const [landingPageHtmlLink, setLandingPageHtmlLink] = useState(
    ourServiceData?.leadCaptureFormData?.formLink || ""
  );
  const [qrCodeURL, setQRCodeURL] = useState("");
  const [resourceId, setResourceId] = useState();

  const getLandingPageDetails = async () => {
    setLoading(true);
    try {
      const response = await fetchLandingPageDetails();
      if (response.status === 200) {
        const responseData = response?.data?.data;
        if (responseData && responseData?.fields_data) {
          setResourceId(responseData?.id);
          const data = JSON.parse(response?.data?.data?.fields_data);
          setMainSectionData(
            data?.sections?.find(
              (section: Section) => section.id === "main-section"
            ) || mainSectionData
          );
          setWhoWeAreData(
            data?.sections?.find(
              (section: Section) => section.id === "who-we-are"
            ) || whoWeAreData
          );
          setWhatWeOfferData(
            data?.sections?.find(
              (section: Section) => section.id === "what-we-offer"
            ) || whatWeOfferData
          );
          setValuesPrinciplesData(
            data?.sections?.find(
              (section: Section) => section.id === "values-principles"
            ) || valuesPrinciplesData
          );
          setPreviousValuesPrinciplesData({
            principles:
              data?.sections?.find(
                (section: Section) => section.id === "values-principles"
              )?.principles ?? valuesPrinciplesData.principles,
          });
          setTestimonialsData(
            data?.sections?.find(
              (section: Section) => section.id === "testimonials"
            ) || testimonialsData
          );
          setPreviousTestMenialData(
            data?.sections?.find(
              (section: Section) => section.id === "testimonials"
            ) || testimonialsData
          );
          setOurServiceData(
            data.sections.find(
              (section: Section) => section.id === "our-service"
            ) || ourServiceData
          );
          setPartnerSuccessMetricsData(
            data.sections.find(
              (section: Section) => section.id === "partner-success-metrics"
            ) || partnerSuccessMetricsData
          );
          // setContactData(data.sections.find((section: Section) => section.id === "contact") || contactData);
          setSectionVisibility({
            "main-section":
              data.sections.find(
                (section: Section) => section.id === "main-section"
              )?.isHided ?? false,
            "who-we-are":
              data.sections.find(
                (section: Section) => section.id === "who-we-are"
              )?.isHided ?? false,
            "what-we-offer":
              data.sections.find(
                (section: Section) => section.id === "what-we-offer"
              )?.isHided ?? false,
            "values-principles":
              data.sections.find(
                (section: Section) => section.id === "values-principles"
              )?.isHided ?? false,
            testimonials:
              data.sections.find(
                (section: Section) => section.id === "testimonials"
              )?.isHided ?? false,
            "our-service":
              data.sections.find(
                (section: Section) => section.id === "our-service"
              )?.isHided ?? false,
            "partner-success-metrics":
              data.sections.find(
                (section: Section) => section.id === "partner-success-metrics"
              )?.isHided ?? false,
          });
        }
      } else {
        setAlertMessage("Failed to fetch landing page details.");
        setAlertType("error");
        setAlertOpen(true);
      }
    } catch (error: any) {
      console.error("Error fetching landing page details:", error);
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while fetching landing page details."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLandingPageDetails();
  }, []);

  useEffect(() => {
    setLandingPageHtmlLink(ourServiceData?.leadCaptureFormData?.formLink || "");
    setQRCodeURL(ourServiceData?.leadCaptureFormData?.qrCodeUrl || "");
  }, [ourServiceData]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div
      className="landing-wrapper"
      style={{ fontFamily: "Roboto, sans-serif" }}
    >
      <div className="breadcrumb-wrapper">
        <BreadcrumbComponent breadcrumbLink={breadcrumbLink} />
      </div>

      <>
        {!isEmailConfigured && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <EmailSetUpBanner />
          </div>
        )}
      </>

      <div className="page-wrapper" style={{ backgroundColor: "white" }}>
        <LandingPageBody
          primaryColor={primaryColor}
          accentColor={secondaryColor}
          logoUrl={logoUrl}
        />
        <MainSection
          isForSharing={false}
          title={mainSectionData?.title}
          mainSectionData={mainSectionData}
          setMainSectionData={setMainSectionData}
          sectionVisibility={sectionVisibility}
          setSectionVisibility={setSectionVisibility}
          primaryColor={primaryColor}
          accentColor={secondaryColor}
          setImageFiles={setImageFiles}
          canWrite={canWrite}
          canDelete={canDelete}
        />
        <WhoWeAre
          title={whoWeAreData.title}
          isForSharing={false}
          whoWeAreData={whoWeAreData}
          setWhoWeAreData={setWhoWeAreData}
          sectionVisibility={sectionVisibility}
          setSectionVisibility={setSectionVisibility}
          primaryColor={primaryColor}
          accentColor={secondaryColor}
          canWrite={canWrite}
          canDelete={canDelete}
        />
        <WhatWeOffer
          title={whatWeOfferData.title}
          isForSharing={false}
          whatWeOfferData={whatWeOfferData}
          setWhatWeOfferData={setWhatWeOfferData}
          sectionVisibility={sectionVisibility}
          setSectionVisibility={setSectionVisibility}
          primaryColor={primaryColor}
          accentColor={secondaryColor}
          setImageFiles={setImageFiles}
          canWrite={canWrite}
          canDelete={canDelete}
        />
        <CoreValuesAndPrinciples
          title={valuesPrinciplesData.title}
          isForSharing={false}
          valuesPrinciplesData={valuesPrinciplesData}
          setValuesPrinciplesData={setValuesPrinciplesData}
          sectionVisibility={sectionVisibility}
          setSectionVisibility={setSectionVisibility}
          primaryColor={primaryColor}
          accentColor={secondaryColor}
          setImageFiles={setImageFiles}
          canWrite={canWrite}
          canDelete={canDelete}
          previousValuesPrinciplesData={previousValuesPrinciplesData}
          setPreviousValuesPrinciplesData={setPreviousValuesPrinciplesData}
        />
        <TestimonialSection
          title={testimonialsData.title}
          isForSharing={false}
          testimonialData={testimonialsData}
          setTestimonialData={setTestimonialsData}
          sectionVisibility={sectionVisibility}
          setSectionVisibility={setSectionVisibility}
          primaryColor={primaryColor}
          accentColor={secondaryColor}
          canWrite={canWrite}
          canDelete={canDelete}
          previousTestMenialData={previousTestMenialData}
          setPreviousTestMenialData={setPreviousTestMenialData}
        />
        <OurServices
          isForSharing={false}
          ourServiceData={ourServiceData}
          setOurServiceData={setOurServiceData}
          sectionVisibility={sectionVisibility}
          setSectionVisibility={setSectionVisibility}
          primaryColor={primaryColor}
          accentColor={secondaryColor}
          canWrite={canWrite}
          canDelete={canDelete}
        />
        <PartnerSuccessMetrics
          isForSharing={false}
          partnerSuccessMetricsData={partnerSuccessMetricsData}
          setPartnerSuccessMetricsData={setPartnerSuccessMetricsData}
          sectionVisibility={sectionVisibility}
          setSectionVisibility={setSectionVisibility}
          primaryColor={primaryColor}
          accentColor={secondaryColor}
          canWrite={canWrite}
          canDelete={canDelete}
        />
        <Contact
          isForSharing={false}
          contactData={contactData}
          setContactData={setContactData}
          primaryColor={primaryColor}
          accentColor={secondaryColor}
        />
        <FooterSection
          primaryColor={primaryColor}
          accentColor={secondaryColor}
          logoUrl={logoUrl}
          companyName={companyName}
          accentBorderColor={accentBorderColor}
        />
      </div>

      <div className="footer">
        {/* <button
          type="button"
          onClick={handleDownload}
          className="cancel-button"
        >
          Download
        </button> */}
        <button
          type="button"
          onClick={handleSave}
          className="cancel-button save-button"
          disabled={!canWrite}
        >
          Save
        </button>
        <button
          type="button"
          onClick={handleSaveAndShare}
          className="save-share-button next-button-wrapper"
          disabled={!canWrite || !isEmailConfigured}
        >
          Save & Share
        </button>
      </div>
      <SaveAndSharePopup
        isShowConfirmationPopup={showSaveAndSharePopup}
        setIsShowConfirmationPopup={setShowSaveAndSharePopup}
        formLink={landingPageHtmlLink}
        setLoading={setLoading}
        setAlertMessage={setAlertMessage}
        setAlertType={setAlertType}
        setAlertOpen={setAlertOpen}
        qrCodeUrl={qrCodeURL}
      />
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default CampaignLandingPage;
